import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, Loader2, ShieldCheck } from "lucide-react";

export default function FileManager({ docType = "document", title = "Documents", testid = "file-manager" }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/files/mine");
      setFiles(data);
    } catch {}
    setLoaded(true);
  };
  if (!loaded) load();

  const pick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max file size 10 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/files/upload?doc_type=${encodeURIComponent(docType)}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded securely");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      toast.success("Removed");
      setFiles(files.filter((f) => f.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || "file";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <Card className="p-5 rounded-2xl" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> {title}</h3>
        <Button size="sm" variant="outline" className="rounded-full" onClick={pick} disabled={uploading} data-testid={`${testid}-upload-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Upload
        </Button>
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onChange} data-testid={`${testid}-input`} />
      </div>
      <div className="mt-3 space-y-2">
        {files.length === 0 && <div className="text-sm text-slate-500 py-2">No files yet. Upload a PDF or image (max 10 MB).</div>}
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 text-sm border-b border-slate-100 pb-2" data-testid={`file-row-${f.id}`}>
            {f.content_type?.startsWith("image/") ? <ImageIcon className="w-4 h-4 text-sky-600" /> : <FileText className="w-4 h-4 text-sky-600" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{f.original_filename}</div>
              <div className="text-xs text-slate-500">{Math.round((f.size || 0) / 1024)} KB · {(f.created_at || "").slice(0, 10)}</div>
            </div>
            <Badge className="bg-slate-100 text-slate-600 border-transparent text-[10px]">{f.doc_type}</Badge>
            <Button size="sm" variant="ghost" onClick={() => download(f)} data-testid={`file-download-${f.id}`}><Download className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(f.id)} data-testid={`file-delete-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
